import { Link } from "react-router";
import { ArrowUpRight } from "lucide-react";
import { Card, Skeleton } from "@bookdental/ui";
import { cn } from "@/lib/cn";

type StatCardProps = {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: React.ReactNode;
  hint?: string;
  /** Cor accent (hex) do ícone/realce. */
  accent: string;
  href?: string;
  /** Alternativa a `href` — ação sob demanda (ex: trocar de organização antes de navegar), em vez
   *  de link direto. Ignorado se `href` também for passado. */
  onClick?: () => void;
  loading?: boolean;
  /** Badge "Em breve" + visual esmaecido pra features sem dado real ainda. */
  comingSoon?: boolean;
  /** Classe extra no wrapper — usada pra um cartão ocupar duas colunas da grade. */
  className?: string;
  /** Cartão que abriu o painel de baixo (ver /organizacoes). */
  selecionado?: boolean;
};

export function StatCard({
  icon: Icon,
  label,
  value,
  hint,
  accent,
  href,
  onClick,
  loading,
  comingSoon,
  className,
  selecionado,
}: StatCardProps) {
  const clickable = !!(href || onClick) && !comingSoon;
  // No celular vira faixa compacta: ícone menor ao lado do número, rótulo pequeno e a dica
  // escondida. Cartão centralizado com número em `text-3xl` é linguagem de desktop — cinco deles
  // comiam meia tela em 360px. Acima de `sm` fica exatamente como era.
  const inner = (
    <Card
      className={cn(
        "relative flex h-full items-center gap-3 overflow-hidden p-3 text-left transition-all",
        "sm:flex-col sm:items-center sm:justify-center sm:gap-0 sm:p-4 sm:text-center",
        clickable && "hover:-translate-y-0.5 hover:shadow-md",
        comingSoon && "opacity-80",
        selecionado && "ring-2 ring-brand-500 dark:ring-primary",
      )}
    >
      <div className="shrink-0 sm:absolute sm:left-3 sm:top-3">
        <span
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg sm:h-10 sm:w-10"
          style={{ backgroundColor: `${accent}1a`, color: accent }}
        >
          <Icon className="h-4 w-4 sm:h-5 sm:w-5" />
        </span>
      </div>

      <div className="absolute right-3 top-3 hidden sm:block">
        {comingSoon ? (
          <span className="rounded-full bg-zinc-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-zinc-500 dark:bg-muted dark:text-muted-foreground">
            Em breve
          </span>
        ) : clickable ? (
          <ArrowUpRight className="h-4 w-4 text-zinc-300 transition-colors group-hover:text-zinc-500" />
        ) : null}
      </div>

      <div className="min-w-0 flex-1 sm:w-full sm:flex-none">
        {loading ? (
          <Skeleton className="h-7 w-10 sm:mx-auto sm:h-8" />
        ) : (
          <p className="text-2xl font-bold leading-none tracking-tight text-zinc-900 sm:text-3xl dark:text-foreground">
            {value}
          </p>
        )}
        <p className="mt-1 text-xs font-medium leading-tight text-zinc-600 sm:truncate sm:text-lg dark:text-foreground/90">
          {label}
        </p>
        {comingSoon && (
          <span className="mt-1 inline-block rounded-full bg-zinc-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-zinc-500 sm:hidden dark:bg-muted dark:text-muted-foreground">
            Em breve
          </span>
        )}
        {hint && (
          <p className="mt-1.5 hidden text-xs leading-snug text-zinc-500 sm:block dark:text-muted-foreground">
            {hint}
          </p>
        )}
      </div>
    </Card>
  );

  if (href && !comingSoon) {
    return (
      <Link to={href} className={cn("group block h-full", className)}>
        {inner}
      </Link>
    );
  }
  if (onClick && !comingSoon) {
    return (
      <button type="button" onClick={onClick} className={cn("group block h-full w-full", className)}>
        {inner}
      </button>
    );
  }
  return <div className={className}>{inner}</div>;
}
